import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Menu, X, LogOut, KeyRound, Bell, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import NotificationsToggle from './NotificationsToggle';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const [nRes, cRes, aRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count'),
        api.get('/announcements')
      ]);
      setNotifications(nRes.data.slice(0, 20));
      setUnreadCount(cRes.data.count);
      setAnnouncements(aRes.data.slice(0, 10));
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch {}
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePasswordChange = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPwLoading(true);
    try {
      await api.put('/auth/change-password', {
        old_password: pwForm.old_password,
        new_password: pwForm.new_password
      });
      toast.success('Password changed successfully');
      setPwDialogOpen(false);
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setPwLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-neutral-200 px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button">
              <Menu className="h-5 w-5 text-neutral-900" />
            </Button>
            <div className="lg:hidden flex items-center">
              <img src="/assets/konattu-logo.png" alt="Konattu HRMS" className="h-8 w-auto object-contain" data-testid="header-logo" />
            </div>
          </div>

          {/* Announcement ticker — scrolls across available middle space */}
          <div className="hidden md:flex flex-1 items-center mx-4 h-9 bg-gradient-to-r from-red-50 via-red-50/70 to-amber-50 rounded-md border border-red-100/70 overflow-hidden" data-testid="announcement-ticker">
            <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 h-full text-[11px] font-bold uppercase tracking-wider flex-shrink-0">
              <Megaphone className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">Announcements</span>
            </div>
            <div className="flex-1 overflow-hidden relative h-full">
              {announcements.length === 0 ? (
                <div className="flex items-center h-full px-4 text-xs text-neutral-700">No announcements at the moment.</div>
              ) : (
                <div
                  className="ticker-track flex items-center gap-10 whitespace-nowrap absolute top-0 left-0 h-full"
                  style={{ animationDuration: `${Math.max(60, announcements.length * 25)}s` }}
                >
                  {[...announcements, ...announcements].map((a, i) => (
                    <div key={`${a.id}-${i}`} className="flex items-center gap-2 text-xs">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="font-semibold text-neutral-950">{a.title}</span>
                      <span className="text-neutral-700">{a.content}</span>
                      <span className="text-[10px] text-neutral-500 mx-1">· {a.created_by_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notification-bell">
                  <Bell className="h-[18px] w-[18px] text-neutral-700" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white" data-testid="notification-count">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
                  <h4 className="text-sm font-semibold text-neutral-950">Notifications</h4>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-xs font-semibold text-red-600 hover:text-red-700" data-testid="mark-all-read">Mark all read</button>
                  )}
                </div>
                <ScrollArea className="max-h-72">
                  {notifications.length === 0 ? (
                    <p className="text-sm text-neutral-500 text-center py-8">No notifications</p>
                  ) : notifications.map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b border-neutral-50 ${!n.read ? 'bg-red-50/40' : ''}`}>
                      <p className="text-xs font-semibold text-neutral-900">{n.title}</p>
                      <p className="text-xs text-neutral-700 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-neutral-500 mt-1">{new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-2.5 hover:bg-neutral-100" data-testid="user-menu-button">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center ring-2 ring-white">
                  <span className="text-white text-xs font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
                </div>
                <span className="hidden sm:inline text-sm font-semibold text-neutral-900">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2.5">
                <p className="text-sm font-semibold text-neutral-950">{user?.name}</p>
                <p className="text-xs text-neutral-700">{user?.email}</p>
                <p className="text-xs text-neutral-500 capitalize mt-0.5">{user?.system_role?.replace('_', ' ')}</p>
              </div>
              <DropdownMenuSeparator />
              <div className="px-1 py-1">
                <NotificationsToggle />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPwDialogOpen(true)} className="cursor-pointer" data-testid="change-password-menu-item">
                <KeyRound className="mr-2 h-4 w-4" /> Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page content — bottom padding accommodates the mobile bottom nav */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-20 lg:pb-8 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation (hidden ≥ lg) */}
      <MobileBottomNav onOpenSidebar={() => setSidebarOpen(true)} />

      {/* Password Change Dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Change Password</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">Update your login password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Current Password</Label>
              <Input type="password" value={pwForm.old_password}
                onChange={e => setPwForm(p => ({ ...p, old_password: e.target.value }))}
                className="rounded-sm" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">New Password</Label>
              <Input type="password" value={pwForm.new_password}
                onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                className="rounded-sm" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm_password}
                onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                className="rounded-sm" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handlePasswordChange} disabled={pwLoading}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="change-password-submit-button">
              {pwLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
